import type { SilaeFormData } from "./silae";

/**
 * Types centralisés pour la gestion des codes générés
 * Évite les répétitions et améliore la cohérence du typage
 */

// ============================================================================
// FORM TYPES
// ============================================================================

/**
 * Valeurs du formulaire de création de code
 */
export interface CreateCodeFormValues {
  name: string;
  description: string;
  tags: string[];
}

/**
 * Données pour créer un nouveau code généré
 */
export interface CreateGeneratedCodePayload {
  name: string;
  description?: string;
  formData: SilaeFormData;
  generatedCode: string;
  /** When true, the backend stores generatedCode as-is without regenerating from formData. Use for Mode B AI scripts. */
  bypassCodeGeneration?: boolean;
  tags?: string[];
  isTemplate?: boolean;
  comment?: string | null;
}

/**
 * Données pour mettre à jour un code généré existant
 */
export interface UpdateGeneratedCodePayload {
  name?: string;
  description?: string;
  formData?: SilaeFormData;
  generatedCode?: string;
  tags?: string[];
  isTemplate?: boolean;
  comment?: string | null;
}

// ============================================================================
// API RESPONSE TYPES
// ============================================================================

/**
 * Code généré complet tel que retourné par l'API
 */
export interface GeneratedCodeEntity {
  id: string;
  userId: string;
  name: string;
  description: string | null;
  formData: SilaeFormData;
  generatedCode: string;
  tags: string[];
  isTemplate: boolean;
  structureType: string | null;
  rubriques: string[];
  comment: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Statistiques des codes générés
 */
export interface GeneratedCodeStats {
  totalCodes: number;
  totalTemplates: number;
  byStructureType: Record<string, number>;
  tagCounts: Record<string, number>;
}

// ============================================================================
// FILTER TYPES
// ============================================================================

/**
 * Filtres pour la récupération des codes générés
 */
export interface GeneratedCodeFilters {
  isTemplate?: boolean;
  tags?: string[];
  structureType?: string;
  search?: string;
  rubriques?: string[];
}

// ============================================================================
// ERROR TYPES
// ============================================================================

/**
 * Erreur API avec structure connue
 */
export interface ApiError {
  message: string;
  statusCode?: number;
  details?: Record<string, unknown>;
}

/**
 * Type guard pour vérifier si une erreur est une ApiError
 */
export const isApiError = (error: unknown): error is ApiError => {
  return (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as ApiError).message === "string"
  );
};

/**
 * Erreur Axios typée
 */
export interface AxiosErrorResponse {
  response?: {
    data?: {
      message?: string;
      error?: string;
    };
    status?: number;
    statusText?: string;
  };
  message?: string;
}

/**
 * Type guard pour vérifier si une erreur est une erreur Axios
 */
export const isAxiosError = (error: unknown): error is AxiosErrorResponse => {
  return typeof error === "object" && error !== null && "response" in error;
};

/**
 * Extrait un message d'erreur lisible depuis n'importe quel type d'erreur
 */
export const extractErrorMessage = (
  error: unknown,
  defaultMessage = "Une erreur inattendue s'est produite",
): string => {
  // Erreur API structurée
  if (isApiError(error)) {
    return error.message;
  }

  // Erreur Axios
  if (isAxiosError(error)) {
    return (
      error.response?.data?.message ||
      error.response?.data?.error ||
      error.message ||
      defaultMessage
    );
  }

  // Erreur standard JavaScript
  if (error instanceof Error) {
    return error.message;
  }

  // Chaîne de caractères
  if (typeof error === "string") {
    return error;
  }

  return defaultMessage;
};

// ============================================================================
// COMPONENT PROPS TYPES
// ============================================================================

/**
 * Props pour le dialog de création de code
 */
export interface CreateCodeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (code: GeneratedCodeEntity) => void;
}

/**
 * Props pour le wizard de code SILAE
 */
export interface SilaeCodeWizardProps {
  codeId?: string | null;
  onCodeSaved?: (code: GeneratedCodeEntity) => void;
  onFinish?: () => void;
  onCancel?: () => void;
}

/**
 * Props pour la vue repository des codes
 */
export interface CodeRepositoryViewProps {
  onEdit: (codeId: string) => void;
  onCreateNew?: () => void;
}

// ============================================================================
// HOOK RETURN TYPES
// ============================================================================

/**
 * Type de retour pour le hook useGeneratedCodes
 */
export interface UseGeneratedCodesReturn {
  codes: GeneratedCodeEntity[];
  isLoading: boolean;
  error: string | null;
  loadCodes: (
    filters?: GeneratedCodeFilters,
    errorMessage?: string,
  ) => Promise<void>;
  reloadCodes: (filters?: GeneratedCodeFilters) => Promise<void>;
  clearError: () => void;
  setCodes: React.Dispatch<React.SetStateAction<GeneratedCodeEntity[]>>;
}

/**
 * Type de retour pour le hook useGeneratedCodeFilters
 */
export interface UseGeneratedCodeFiltersReturn {
  searchQuery: string;
  rubriqueFilter: string;
  tagFilter: string;
  showTemplatesOnly: boolean;
  setSearchQuery: (query: string) => void;
  setRubriqueFilter: (filter: string) => void;
  setTagFilter: (filter: string) => void;
  setShowTemplatesOnly: (show: boolean) => void;
  filters: GeneratedCodeFilters;
  hasActiveFilters: boolean;
  resetFilters: () => void;
}

// ============================================================================
// VALIDATION TYPES
// ============================================================================

/**
 * Résultat de validation
 */
export interface ValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
}

/**
 * Options de validation
 */
export interface ValidationOptions {
  abortEarly?: boolean;
  stripUnknown?: boolean;
}

// ============================================================================
// UTILITY TYPES
// ============================================================================

/**
 * Rend tous les champs optionnels sauf ceux spécifiés
 */
export type PartialExcept<T, K extends keyof T> = Partial<T> & Pick<T, K>;

/**
 * Rend tous les champs requis sauf ceux spécifiés
 */
export type RequiredExcept<T, K extends keyof T> = Required<Omit<T, K>> &
  Pick<T, K>;

/**
 * Type pour les actions asynchrones
 */
export type AsyncAction<T = void> = () => Promise<T>;

/**
 * Type pour les callbacks asynchrones avec paramètre
 */
export type AsyncCallback<P, R = void> = (param: P) => Promise<R>;
